import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  Camera,
  Send,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Star,
} from 'lucide-react';

interface SurveyQuestion {
  id: string;
  text: string;
  type: 'YES_NO' | 'SCALE' | 'TEXT' | 'SINGLE_CHOICE' | 'MULTI_CHOICE' | 'PHOTO';
  required: boolean;
  options?: string[];
  order: number;
}

interface SurveyData {
  id: string;
  title: string;
  description?: string;
  questions: SurveyQuestion[];
}

export default function Survey({
  surveyId,
  visitId,
  storeId,
}: {
  surveyId: string;
  visitId?: string;
  storeId?: string;
}) {
  const [, setLocation] = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [showReview, setShowReview] = useState(false);

  // Fetch survey data
  const { data: survey, isLoading, error } = useQuery<SurveyData>({
    queryKey: ['survey', surveyId],
    queryFn: () => api.get(`/surveys/${surveyId}`).then(res => res.data.data),
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      // Flatten answers: multi_choice stays as array, rest as string
      const flatAnswers: Record<string, string | string[]> = {};
      for (const [questionId, value] of Object.entries(answers)) {
        flatAnswers[questionId] = value;
      }

      const payload: Record<string, unknown> = {
        survey_id: surveyId,
        answers: flatAnswers,
      };

      if (visitId) payload.visit_id = visitId;
      if (storeId) payload.store_id = storeId;

      const response = await api.post('/surveys/respond', payload);
      return response.data.data;
    },
    onSuccess: () => {
      if (visitId && storeId) {
        setLocation(`/visit/${storeId}`);
      } else if (storeId) {
        // Pesquisa PDV avulsa (sem visita) — volta para a escolha de loja
        setLocation('/pesquisa-pdv');
      } else {
        setLocation('/route');
      }
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Erro ao enviar pesquisa');
    },
  });

  const sortedQuestions = useMemo(() => {
    if (!survey?.questions) return [];
    return [...survey.questions].sort((a, b) => a.order - b.order);
  }, [survey]);

  const totalQuestions = sortedQuestions.length;
  const currentQuestion = sortedQuestions[currentStep];

  const answeredCount = useMemo(() => {
    return sortedQuestions.filter(q => {
      const answer = answers[q.id];
      if (answer === undefined || answer === null) return false;
      if (Array.isArray(answer)) return answer.length > 0;
      return answer !== '';
    }).length;
  }, [sortedQuestions, answers]);

  const progressPercentage = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const canGoNext = () => {
    if (!currentQuestion) return false;
    if (!currentQuestion.required) return true;
    const answer = answers[currentQuestion.id];
    if (answer === undefined || answer === null) return false;
    if (Array.isArray(answer)) return answer.length > 0;
    return answer !== '';
  };

  const canSubmit = () => {
    return sortedQuestions.every(q => {
      if (!q.required) return true;
      const answer = answers[q.id];
      if (answer === undefined || answer === null) return false;
      if (Array.isArray(answer)) return answer.length > 0;
      return answer !== '';
    });
  };

  const handleAnswer = (questionId: string, value: string | string[]) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultiChoiceToggle = (questionId: string, option: string) => {
    setAnswers(prev => {
      const current = (prev[questionId] as string[]) || [];
      if (current.includes(option)) {
        return { ...prev, [questionId]: current.filter(o => o !== option) };
      }
      return { ...prev, [questionId]: [...current, option] };
    });
  };

  const handleNext = () => {
    if (currentStep < totalQuestions - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowReview(true);
    }
  };

  const handlePrevious = () => {
    if (showReview) {
      setShowReview(false);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    submitMutation.mutate();
  };

  const getAnswerDisplayValue = (question: SurveyQuestion): string => {
    const answer = answers[question.id];
    if (answer === undefined || answer === null) return 'Nao respondida';
    if (Array.isArray(answer)) return answer.length > 0 ? answer.join(', ') : 'Nao respondida';
    if (question.type === 'YES_NO') return answer === 'yes' ? 'Sim' : 'Nao';
    if (question.type === 'SCALE') {
      const opts = question.options as any;
      const max = opts?.max ?? 5;
      return `${answer} de ${max}`;
    }
    return answer || 'Nao respondida';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando pesquisa...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !survey) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Erro ao carregar pesquisa</h2>
        <p className="text-gray-600 mb-6 text-center">
          Nao foi possivel carregar os dados da pesquisa
        </p>
        <button
          onClick={() => setLocation('/route')}
          className="px-6 py-3 bg-primary text-white rounded-lg font-semibold"
        >
          Voltar
        </button>
      </div>
    );
  }

  // Empty questions state
  if (sortedQuestions.length === 0) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <header className="bg-primary text-white p-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLocation('/route')}
              className="p-2 -ml-2 rounded-full hover:bg-white/20"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-bold">{survey.title}</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhuma pergunta disponivel
            </h2>
            <p className="text-gray-600 mb-6">
              Esta pesquisa nao possui perguntas cadastradas
            </p>
            <button
              onClick={() => setLocation('/route')}
              className="px-6 py-3 bg-primary text-white rounded-lg font-semibold"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Review step
  if (showReview) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-primary text-white p-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handlePrevious}
              className="p-2 -ml-2 rounded-full hover:bg-white/20"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-bold">{survey.title}</h1>
              <p className="text-sm opacity-80">Revisao das respostas</p>
            </div>
          </div>
        </header>

        {/* Progress bar - full */}
        <div className="h-1.5 bg-gray-200">
          <div className="h-full bg-green-500 transition-all" style={{ width: '100%' }} />
        </div>

        {/* Review Content */}
        <main className="flex-1 p-4 pb-28 space-y-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Star className="h-5 w-5 text-primary" />
              <h2 className="font-semibold text-gray-900">Resumo</h2>
            </div>
            <p className="text-sm text-gray-600">
              {answeredCount} de {totalQuestions} pergunta(s) respondida(s)
            </p>
          </div>

          {sortedQuestions.map((question, index) => {
            const answered = (() => {
              const a = answers[question.id];
              if (a === undefined || a === null) return false;
              if (Array.isArray(a)) return a.length > 0;
              return a !== '';
            })();

            return (
              <button
                key={question.id}
                onClick={() => {
                  setShowReview(false);
                  setCurrentStep(index);
                }}
                className="w-full text-left bg-white rounded-xl shadow-sm border border-gray-100 p-4"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-full flex-shrink-0 mt-0.5 ${answered ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {answered ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-500 mb-0.5">
                      Pergunta {index + 1}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    <p className="font-medium text-gray-900 text-sm truncate">{question.text}</p>
                    <p className={`text-sm mt-1 ${answered ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                      {getAnswerDisplayValue(question)}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                </div>
              </button>
            );
          })}
        </main>

        {/* Fixed Submit Button */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
          <button
            onClick={handleSubmit}
            disabled={submitMutation.isPending || !canSubmit()}
            className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>Enviar Pesquisa</span>
              </>
            )}
          </button>
          {!canSubmit() && (
            <p className="text-xs text-red-500 text-center mt-2">
              Responda todas as perguntas obrigatorias antes de enviar
            </p>
          )}
        </div>
      </div>
    );
  }

  // Question step
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              if (currentStep === 0) {
                if (visitId && storeId) {
                  setLocation(`/visit/${storeId}`);
                } else {
                  setLocation('/route');
                }
              } else {
                handlePrevious();
              }
            }}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold truncate">{survey.title}</h1>
            <p className="text-sm opacity-80">
              {currentStep + 1} de {totalQuestions}
            </p>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-200">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      {/* Question Content */}
      <main className="flex-1 p-4 pb-28">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          {/* Question Number & Required */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">
              {currentStep + 1}/{totalQuestions}
            </span>
            {currentQuestion.required && (
              <span className="text-xs font-semibold text-red-500 bg-red-50 px-2 py-1 rounded-full">
                Obrigatoria
              </span>
            )}
          </div>

          {/* Question Text */}
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {currentQuestion.text}
          </h2>

          {/* Answer Input based on type */}
          {currentQuestion.type === 'YES_NO' && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleAnswer(currentQuestion.id, 'yes')}
                className={`flex flex-col items-center gap-3 py-6 rounded-xl border-2 font-semibold transition-colors ${
                  answers[currentQuestion.id] === 'yes'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-700 active:bg-gray-50'
                }`}
              >
                <CheckCircle className={`h-10 w-10 ${
                  answers[currentQuestion.id] === 'yes' ? 'text-green-500' : 'text-gray-300'
                }`} />
                <span className="text-base">Sim</span>
              </button>
              <button
                onClick={() => handleAnswer(currentQuestion.id, 'no')}
                className={`flex flex-col items-center gap-3 py-6 rounded-xl border-2 font-semibold transition-colors ${
                  answers[currentQuestion.id] === 'no'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 text-gray-700 active:bg-gray-50'
                }`}
              >
                <XCircle className={`h-10 w-10 ${
                  answers[currentQuestion.id] === 'no' ? 'text-red-500' : 'text-gray-300'
                }`} />
                <span className="text-base">Nao</span>
              </button>
            </div>
          )}

          {currentQuestion.type === 'SCALE' && (() => {
            const opts = currentQuestion.options as any;
            const min = opts?.min ?? 1;
            const max = opts?.max ?? 5;
            const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
            const isWide = values.length > 6;
            return (
              <div className={`flex items-center justify-center ${isWide ? 'gap-1' : 'gap-2'} flex-wrap`}>
                {values.map(value => {
                  const selected = answers[currentQuestion.id] === String(value);
                  return (
                    <button
                      key={value}
                      onClick={() => handleAnswer(currentQuestion.id, String(value))}
                      className={`${isWide ? 'w-10 h-10 text-sm' : 'w-14 h-14 text-lg'} rounded-xl border-2 font-bold transition-colors ${
                        selected
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-200 text-gray-700 active:bg-gray-50'
                      }`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {currentQuestion.type === 'TEXT' && (
            <textarea
              value={(answers[currentQuestion.id] as string) || ''}
              onChange={e => handleAnswer(currentQuestion.id, e.target.value)}
              placeholder="Digite sua resposta..."
              rows={5}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
            />
          )}

          {currentQuestion.type === 'SINGLE_CHOICE' && currentQuestion.options && (
            <div className="space-y-2">
              {currentQuestion.options.map(option => {
                const selected = answers[currentQuestion.id] === option;
                return (
                  <button
                    key={option}
                    onClick={() => handleAnswer(currentQuestion.id, option)}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-colors ${
                      selected
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 text-gray-700 active:bg-gray-50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selected ? 'border-primary' : 'border-gray-300'
                    }`}>
                      {selected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>
                    <span className="font-medium">{option}</span>
                  </button>
                );
              })}
            </div>
          )}

          {currentQuestion.type === 'MULTI_CHOICE' && currentQuestion.options && (
            <div className="space-y-2">
              {currentQuestion.options.map(option => {
                const currentValues = (answers[currentQuestion.id] as string[]) || [];
                const selected = currentValues.includes(option);
                return (
                  <button
                    key={option}
                    onClick={() => handleMultiChoiceToggle(currentQuestion.id, option)}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-colors ${
                      selected
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 text-gray-700 active:bg-gray-50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                      selected ? 'border-primary bg-primary' : 'border-gray-300'
                    }`}>
                      {selected && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                    </div>
                    <span className="font-medium">{option}</span>
                  </button>
                );
              })}
            </div>
          )}

          {currentQuestion.type === 'PHOTO' && (
            <div className="space-y-4">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                id={`photo-input-${currentQuestion.id}`}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const formData = new FormData();
                    formData.append('photo', file, file.name);
                    formData.append('photo_type', 'SURVEY');
                    const { default: api } = await import('../lib/api');
                    const res = await api.post('/photos/upload', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    handleAnswer(currentQuestion.id, res.data?.data?.url || file.name);
                  } catch {
                    // Fallback: use local object URL if upload fails
                    handleAnswer(currentQuestion.id, URL.createObjectURL(file));
                  }
                }}
              />
              <button
                onClick={() => document.getElementById(`photo-input-${currentQuestion.id}`)?.click()}
                className={`w-full flex flex-col items-center gap-3 py-8 rounded-xl border-2 border-dashed transition-colors ${
                  answers[currentQuestion.id]
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 bg-gray-50 active:bg-gray-100'
                }`}
              >
                {answers[currentQuestion.id] ? (
                  <>
                    <CheckCircle className="h-12 w-12 text-green-500" />
                    <span className="font-medium text-green-700">Foto capturada</span>
                    <span className="text-sm text-green-600">Toque para alterar</span>
                  </>
                ) : (
                  <>
                    <Camera className="h-12 w-12 text-gray-400" />
                    <span className="font-medium text-gray-700">Tirar Foto</span>
                    <span className="text-sm text-gray-500">Toque para abrir a camera</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Fixed Navigation Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="flex items-center justify-center gap-1 px-5 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5" />
            <span>Anterior</span>
          </button>

          <button
            onClick={handleNext}
            disabled={currentQuestion.required && !canGoNext()}
            className="flex-1 flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep < totalQuestions - 1 ? (
              <>
                <span>Proxima</span>
                <ChevronRight className="h-5 w-5" />
              </>
            ) : (
              <>
                <span>Revisar</span>
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
